// 템플릿 카탈로그 — 가져오기 '템플릿 양식' 프리셋이 쓰는 전역 기본 템플릿 목록·스키마·data_types(spec D2)
import i18n from 'i18next'
import ResourceTemplateService from 'api/ResourceTemplate'
import { fetchTemplateSchema } from 'utils/importExport/bulkImport'
import { isMemoTemplate } from 'pages/records/components/MemoEditorModal/memoRecord'
import { shouldHideAuthenticatorTemplate } from 'pages/records/components/authenticatorTemplate'

// 서버 limit 상한이다(backend/routers/v1/resource.py resource_template_list 의 limit le=100)
const PAGE_SIZE = 100
const MAX_PAGES = 10

// 목록은 전역·회사 템플릿을 이름순으로 섞어 자르므로 첫 페이지만 보면 기본 템플릿이 잘릴 수 있다 — total 까지
// 순회한다. 끝은 앞 응답으로 정해지므로 페이지는 순차로 부른다. 인자는 { params } 로 감싸지 않은 평탄한 객체여야 한다
// (감싸면 쿼리가 조용히 사라진다 — frontend/docs/wiki/domain/rules/api-wiring-silent-failures.md)
const collectTemplatePages = async (pageIndex, collected) => {
  // offset 은 지금까지 받은 개수로 잇는다 — 서버는 limit 이 LIST_MAX_LIMIT 를 넘으면 조용히 그 값으로 자르므로
  // (backend/database/models/resource_template.py:118-119) 페이지 크기를 100 으로 가정하면 그 사이를 건너뛴다
  const response = await ResourceTemplateService.getAll({
    offset: collected.templates.length,
    limit: PAGE_SIZE,
  })
  const pageTemplates = response.data ?? []
  const merged = {
    templates: [...collected.templates, ...pageTemplates],
    // data_types 는 전역 정의라 첫 페이지 것을 쓴다
    dataTypes: pageIndex === 0 ? (response.data_types ?? []) : collected.dataTypes,
    // translations.data 에는 그 페이지의 템플릿만 담기므로 페이지마다 병합한다
    translationsData: { ...collected.translationsData, ...(response.translations?.data ?? {}) },
  }
  if (pageTemplates.length === 0 || merged.templates.length >= response.total) return merged
  if (pageIndex + 1 >= MAX_PAGES) {
    console.warn(`Template catalog truncated after ${MAX_PAGES} pages`)
    return merged
  }
  return collectTemplatePages(pageIndex + 1, merged)
}

// 생성 패널의 템플릿 셀렉트와 같은 규칙으로 번역한다(helper.js 의 titleTranslations·makeLangMap·getLabelMap):
// 현재 로케일 정확 키 → 앞 2글자가 같은 키 → 영문 이름. 세 번째 방식을 만들면 select 와 표기가 어긋난다
// (frontend/docs/wiki/domain/rules/template-display-names.md)
const getDisplayName = (template, translationsData) => {
  const translationsByLocale = translationsData[template.id] || {}
  const locale = (i18n.language || 'ko-KR').replace('-', '_')
  const matchedKey = translationsByLocale[locale]
    ? locale
    : Object.keys(translationsByLocale).find((key) => key.startsWith(locale.slice(0, 2)))
  const labelMap = (matchedKey && translationsByLocale[matchedKey]) || {}
  return labelMap[template.name] || template.name
}

export const fetchTemplateCatalog = async () => {
  const { templates, dataTypes, translationsData } = await collectTemplatePages(0, {
    templates: [],
    dataTypes: [],
    translationsData: {},
  })
  // 목록을 여러 페이지로 받는 사이 앞쪽에 항목이 추가되면 경계 항목이 두 번 온다(offset 조회) — 같은 id 는 한 번만
  // 둔다. 두 번 두면 같은 템플릿끼리 판별 동률이 되어 Default 로 떨어진다(templateForm.js:78-81)
  const uniqueTemplates = [
    ...new Map(templates.map((template) => [template.id, template])).values(),
  ]
  // 기존 생성 패널과 같은 기준이다. 목록 단계에서 거르므로 숨길 템플릿의 상세는 아예 조회하지 않는다
  const visibleTemplates = uniqueTemplates.filter(
    (template) =>
      template.readonly === true &&
      !isMemoTemplate(template) &&
      !shouldHideAuthenticatorTemplate(template),
  )
  // 하나라도 실패하면 카탈로그 전체가 실패한다 — 부분 성공을 허용하면 자동 판별이 목록에 없는 템플릿을 못 보고
  // 다른 템플릿을 고른다(spec D2)
  const schemas = await Promise.all(
    visibleTemplates.map((template) => fetchTemplateSchema(template.id)),
  )
  return {
    items: visibleTemplates.map((template, index) => ({
      id: template.id,
      name: template.name,
      displayName: getDisplayName(template, translationsData),
      schema: schemas[index],
    })),
    dataTypes,
  }
}

// 응답 name 은 항상 영문 title 이다 — 목록 라우터가 locale 을 en_US 로 고정한다
// (backend/routers/v1/resource.py resource_template_list, frontend wiki template-display-names §3)
export const findDefaultTemplateId = (items) =>
  items.find((item) => item.name === 'Default')?.id ?? null
